import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faCircle } from '@fortawesome/free-solid-svg-icons';
import Table from '../../components/Listing/Table';
import Button from '../../components/Form/Button';
import Modal from '../../components/Modal/Modal';
import { usePageTitle } from '../../layouts/PageTitleContext';
import { API_BASE_URL } from '../../api';

export default function BackofficeContactMessages() {
    const { setTitle, setTitleIcon, setTitleDescription } = usePageTitle();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [keyword, setKeyword] = useState('');
    const [selected, setSelected] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        return axios.get(`${API_BASE_URL}/api/v1/backoffice/contact-messages`, { withCredentials: true })
            .then(({ data }) => setMessages(data))
            .catch(() => toast.error('Impossible de charger les messages.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        document.title = 'Backoffice — Messages de contact';
        load();
    }, [load]);

    useEffect(() => {
        setTitle('Messages de contact');
        setTitleDescription('Demandes envoyées depuis le formulaire de contact du site public');
        setTitleIcon('ti-mail');
        return () => { setTitleDescription(null); setTitleIcon(null); };
    }, [setTitle, setTitleDescription, setTitleIcon]);

    const openMessage = (row) => {
        setSelected(row);
        if (!row.read) {
            axios.patch(`${API_BASE_URL}/api/v1/backoffice/contact-messages/${row.id}/read`, null, { withCredentials: true })
                .then(() => setMessages(prev => prev.map(m => (m.id === row.id ? { ...m, read: true } : m))))
                .catch(() => {});
        }
    };

    const handleDelete = (row) => {
        axios.delete(`${API_BASE_URL}/api/v1/backoffice/contact-messages/${row.id}`, { withCredentials: true })
            .then(() => {
                toast.success('Message supprimé.');
                load();
            })
            .catch(() => toast.error('Une erreur est survenue.'));
    };

    const columns = [
        {
            accessor: 'read',
            Header: '',
            minWidth: 30,
            Cell: row => !row.read && (
                <FontAwesomeIcon icon={faCircle} style={{ fontSize: 8, color: '#2563eb' }} title="Non lu" />
            ),
        },
        {
            accessor: 'name',
            Header: 'Nom',
            sortable: true,
            Cell: row => <span style={{ fontWeight: row.read ? 400 : 700 }}>{row.name}</span>,
        },
        { accessor: 'email', Header: 'Email', sortable: true },
        { accessor: 'phone', Header: 'Téléphone', Cell: row => row.phone || '—' },
        { accessor: 'pharmacy', Header: 'Pharmacie', Cell: row => row.pharmacy || '—' },
        {
            accessor: 'message',
            Header: 'Message',
            Cell: row => (
                <span style={{ fontSize: 13, color: '#6b7280' }}>
                    {row.message.length > 80 ? `${row.message.slice(0, 80)}…` : row.message}
                </span>
            ),
        },
        {
            accessor: 'createdAt',
            Header: 'Reçu le',
            sortable: true,
            Cell: row => new Date(row.createdAt).toLocaleString('fr-FR'),
        },
        {
            accessor: 'actions',
            Header: '',
            className: 'col-actions',
            Cell: row => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <button className="dropdown-item" onClick={() => openMessage(row)}>Voir le message</button>
                        </li>
                        <li>
                            <Button
                                className="dropdown-item text-danger"
                                onConfirm={() => handleDelete(row)}
                            >
                                Supprimer
                            </Button>
                        </li>
                    </ul>
                </div>
            ),
        },
    ];

    return (
        <>
            <Table
                columns={columns}
                data={messages}
                mode="client"
                loading={loading}
                keyword={keyword}
                setKeyword={setKeyword}
                searchPlaceholder="Rechercher un message..."
                rowId="id"
                pageSize={20}
                emptyMessage="Aucun message de contact"
                onRowClick={(row) => openMessage(row)}
            />

            <Modal
                show={!!selected}
                onClose={() => setSelected(null)}
                title={selected?.name}
            >
                {selected && (
                    <div>
                        <p><strong>Email :</strong> {selected.email}</p>
                        <p><strong>Téléphone :</strong> {selected.phone || '—'}</p>
                        <p><strong>Pharmacie :</strong> {selected.pharmacy || '—'}</p>
                        <p><strong>Reçu le :</strong> {new Date(selected.createdAt).toLocaleString('fr-FR')}</p>
                        <hr />
                        <p style={{ whiteSpace: 'pre-wrap' }}>{selected.message}</p>
                    </div>
                )}
            </Modal>
        </>
    );
}
